import {
  RenewalAction,
  Status,
  Entitlement,
  SubscriptionState,
  BillingStatus,
  Lifecycle,
  SubscriptionType,
} from "./subscriptionState";

/**
 * The fields (directly matching those stored in the DB) which
 * are used to determine the state of the subscription.
 */
export type SubscriptionParamsForState = {
  isCancelled?: boolean;
  ends: Date;
  stripeLastStatus?: string;
  stripeSubscriptionId?: string;

  // The type is not needed, we just need to know if this field is present on
  // the subscription object or not. This way you can provide either the
  // string id, the mongo id, or the populated object.
  downgradeToPlan?: unknown;
};

export function getSubscriptionState(
  subscription: SubscriptionParamsForState,
): SubscriptionState {
  const now = new Date();

  const type = parseType(subscription);

  const periodEnd = subscription.ends;

  const lifecycle = parseLifecycle(subscription, now);

  const renewal = parseRenewal(subscription, now);

  const entitlement = parseEntitlement(subscription, now);

  const billing = parseBillingStatus(subscription);

  const status = parseStatus(lifecycle, renewal, billing);

  return {
    type,
    periodEnd,
    status,
    renewal,
    entitlement,
    billing,
    lifecycle,
  };
}

function parseType(subscription: SubscriptionParamsForState): SubscriptionType {
  if (subscription.stripeSubscriptionId) {
    return SubscriptionType.Stripe;
  }

  return SubscriptionType.Manual;
}

function parseBillingStatus(
  subscription: SubscriptionParamsForState,
): BillingStatus {
  // stripe-managed plan
  if (subscription.stripeSubscriptionId) {
    // these states all indicate there is an overdue invoice which the user
    // needs to resolve.
    if (
      subscription.stripeLastStatus === "past_due" ||
      subscription.stripeLastStatus === "incomplete" ||
      subscription.stripeLastStatus === "unpaid"
    ) {
      return BillingStatus.Unpaid;
    }

    // other states are either paid, or the subscription has been terminated.
    return BillingStatus.Ok;
  }

  // the manual plan doesn't have billing, so it's always considered okay.
  return BillingStatus.Ok;
}

/**
 * Is the user currently entitled to use the subscription?
 * i.e. is billing up to date
 *
 * For stripe-managed plans, we use the subscription 'status' from Stripe to
 * determine if the subscription is in good standing or not.
 * https://docs.stripe.com/billing/subscriptions/overview#subscription-statuses
 *
 * - 'active' means the current period has been paid, the subscription is in
 * good standing.
 *
 * - 'past_due' means the recurring payment failed, but Stripe is still
 * attempting payment retries. We can be in this state up to 2 weeks currently,
 * before it finally moves to the 'unpaid' state. We previously allowed a grace
 * period while in this state, but it added unnecessary complexity to the logic
 * and was removed.
 *
 * 'incomplete', 'unpaid', mean the invoice for the current period has not
 * been paid, but the user can still make payment to resume the subscription.
 * The subscription is not in good standing.
 *
 * 'incomplete_expired' and 'canceled' are terminal states, indicating the
 * subscription is terminated and cannot be resumed. The subscription is not
 * in good standing.
 *
 * 'trialing' and 'paused' are only used for trial subscriptions in Stripe.
 * Since we don't use that feature, they can be ignored.
 */
function parseEntitlement(subscription: SubscriptionParamsForState, now: Date) {
  // stripe-managed plan
  if (subscription.stripeSubscriptionId) {
    if (subscription.stripeLastStatus === "active") {
      return Entitlement.Entitled;
    }

    if (subscription.stripeLastStatus === "past_due") {
      return Entitlement.Suspended;
    }

    return Entitlement.Suspended;
  }

  // manual plan
  if (now <= subscription.ends) {
    return Entitlement.Entitled;
  }

  return Entitlement.Suspended;
}

function parseRenewal(subscription: SubscriptionParamsForState, now: Date) {
  // stripe-managed plan
  if (subscription.stripeSubscriptionId) {
    // these are the only two states in Stripe which mean the subscription has
    // completely terminated.
    if (
      subscription.stripeLastStatus === "canceled" ||
      subscription.stripeLastStatus === "incomplete_expired"
    ) {
      return RenewalAction.None;
    }

    // both cancelled and downgrading being true should never be possible, but
    // if it happens, we prioritise the cancel state.
    if (subscription.isCancelled) {
      return RenewalAction.Cancel;
    }

    if (subscription.downgradeToPlan) {
      return RenewalAction.Downgrade;
    }

    return RenewalAction.Renew;
  }

  // manual plan has either already expired or will cancel and expire at
  // the end of the current period. Renew and Downgrade don't exist for manual.
  if (now > subscription.ends) {
    return RenewalAction.None;
  }

  return RenewalAction.Cancel;
}

function parseLifecycle(subscription: SubscriptionParamsForState, now: Date) {
  // stripe-managed subscription
  if (subscription.stripeSubscriptionId) {
    // these are the only two states in Stripe which mean the subscription has
    // completely terminated.
    if (
      subscription.stripeLastStatus === "canceled" ||
      subscription.stripeLastStatus === "incomplete_expired"
    ) {
      return Lifecycle.Terminated;
    }

    return Lifecycle.Active;
  }

  // manual subscription
  if (now > subscription.ends) {
    return Lifecycle.Terminated;
  }

  return Lifecycle.Active;
}

function parseStatus(
  lifecycle: Lifecycle,
  renewal: RenewalAction,
  billing: BillingStatus,
): Status {
  if (lifecycle === Lifecycle.Terminated) {
    return Status.Expired;
  }

  if (billing === BillingStatus.Unpaid) {
    return Status.RequiresAction;
  }

  if (renewal === RenewalAction.Cancel) {
    return Status.Cancelled;
  }

  if (renewal === RenewalAction.Downgrade) {
    return Status.DowngradeScheduled;
  }

  return Status.Active;
}
